import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, User, Calendar, ChevronRight, ChevronLeft, Trash2, AlertTriangle, LayoutGrid, List, EyeOff, Check, AlertCircle as AlertCircleIcon, ShieldCheck, RotateCcw, ShieldAlert } from 'lucide-react';
import type { UserEntry, MonthlyApproval, MfaAdminUserStatus, MfaStatus } from '@/types';
import { EMPLOYMENT_TYPE_LABELS, MFA_STATUS_LABELS } from '@/types';
import { prevPayrollMonth, nextPayrollMonth, formatPayrollMonth } from '@/utils/payrollPeriod';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { getStatusBadgeVariant } from '@/lib/status-colors';
import { APIStorage } from '@/services/apiStorage';

type EmploymentStatus = 'active' | 'ending-soon' | 'departed';

function getEmploymentStatus(user: UserEntry): EmploymentStatus {
  if (!user.employmentEnd) return 'active';
  const endDate = new Date(user.employmentEnd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  if (endDate < today) return 'departed';
  const thirtyDays = new Date(today);
  thirtyDays.setDate(thirtyDays.getDate() + 30);
  if (endDate <= thirtyDays) return 'ending-soon';
  return 'active';
}

interface Props {
  users: UserEntry[];
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
  onDeleteUser?: (userId: string) => Promise<void>;
  onCalendarEdit?: (user: UserEntry) => void;
  isLoading: boolean;
  showDeleteButton?: boolean;
  // Freigabe-Props (nur HR)
  isHR?: boolean;
  approvalMonth?: { year: number; month: number };
  onApprovalMonthChange?: (m: { year: number; month: number }) => void;
  approvals?: MonthlyApproval[];
  onApprovalChange?: (userId: string, approved: boolean, note: string) => Promise<void>;
  onViewModeChange?: (mode: 'list' | 'grid') => void;
}

export function EmployeeList({ users, selectedUserId, onSelectUser, onDeleteUser, onCalendarEdit, isLoading, showDeleteButton = true, isHR, approvalMonth, onApprovalMonthChange, approvals, onApprovalChange, onViewModeChange }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<Set<EmploymentStatus>>(new Set());
  const [userToDelete, setUserToDelete] = useState<UserEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // MFA Admin State
  const [mfaOverview, setMfaOverview] = useState<Map<string, MfaAdminUserStatus>>(new Map());
  const [mfaAction, setMfaAction] = useState<{ type: 'reset' | 'force'; user: UserEntry } | null>(null);
  const [isMfaActionPending, setIsMfaActionPending] = useState(false);
  const [mfaActionError, setMfaActionError] = useState<string | null>(null);
  const api = useRef(new APIStorage()).current;

  // Lokaler State für Anmerkungsfelder (Debounce: speichern onBlur, nicht bei jedem Tastenanschlag)
  const [localNotes, setLocalNotes] = useState<Record<string, string>>({});
  // Visuelles Feedback nach Speichern: userId → 'success' | 'error'
  const [saveStatus, setSaveStatus] = useState<Record<string, 'success' | 'error'>>({});

  // ViewMode an Parent melden
  useEffect(() => {
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  // MFA-Übersicht laden (nur HR)
  const loadMfaOverview = useCallback(async () => {
    if (!isHR) return;
    try {
      const data = await api.mfaAdminOverview();
      const map = new Map<string, MfaAdminUserStatus>();
      for (const u of data.users) map.set(u.userId, u);
      setMfaOverview(map);
    } catch (e) {
      console.error('Fehler beim Laden der MFA-Übersicht:', e);
    }
  }, [isHR, api]);

  useEffect(() => {
    void loadMfaOverview();
  }, [loadMfaOverview]);

  const handleMfaAction = async () => {
    if (!mfaAction) return;
    setIsMfaActionPending(true);
    setMfaActionError(null);
    try {
      if (mfaAction.type === 'reset') {
        await api.mfaAdminReset(mfaAction.user.id);
      } else {
        await api.mfaAdminForce(mfaAction.user.id);
      }
      await loadMfaOverview();
      setMfaAction(null);
    } catch (e) {
      // Dialog offen lassen + Fehler zeigen, damit der HR-User erkennt
      // dass die Aktion nicht erfolgreich war.
      console.error('Fehler bei MFA-Aktion:', e);
      setMfaActionError(e instanceof Error ? e.message : 'MFA-Aktion fehlgeschlagen.');
    } finally {
      setIsMfaActionPending(false);
    }
  };

  // Lokale Notes synchronisieren wenn Approvals sich ändern
  useEffect(() => {
    if (!approvals) return;
    const notes: Record<string, string> = {};
    for (const a of approvals) {
      notes[a.userId] = a.approvalNote ?? '';
    }
    setLocalNotes(notes);
  }, [approvals]);

  // Approval-Lookup
  const approvalMap = useMemo(() => {
    const map = new Map<string, MonthlyApproval>();
    if (approvals) {
      for (const a of approvals) map.set(a.userId, a);
    }
    return map;
  }, [approvals]);

  // Pro-User setTimeout-Refs, damit beim Unmount alle Timer abgebrochen
  // werden (kein setState auf unmounteter Komponente).
  const feedbackTimeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = feedbackTimeoutsRef.current;
    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);

  const showApprovalFeedback = useCallback((userId: string, status: 'success' | 'error') => {
    const existing = feedbackTimeoutsRef.current.get(userId);
    if (existing) clearTimeout(existing);

    setSaveStatus((prev) => ({ ...prev, [userId]: status }));
    const timer = setTimeout(() => {
      setSaveStatus((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      feedbackTimeoutsRef.current.delete(userId);
    }, 1500);
    feedbackTimeoutsRef.current.set(userId, timer);
  }, []);

  const handleCheckboxChange = async (userId: string, checked: boolean) => {
    if (!onApprovalChange) return;
    try {
      const note = localNotes[userId] ?? approvalMap.get(userId)?.approvalNote ?? '';
      await onApprovalChange(userId, checked, note);
      showApprovalFeedback(userId, 'success');
    } catch (e) {
      console.error('Fehler beim Speichern der Freigabe:', e);
      showApprovalFeedback(userId, 'error');
    }
  };

  const handleNoteBlur = async (userId: string) => {
    if (!onApprovalChange) return;
    const approval = approvalMap.get(userId);
    const newNote = localNotes[userId] ?? '';
    // Nur speichern wenn sich die Anmerkung geändert hat
    if (newNote === (approval?.approvalNote ?? '')) return;
    try {
      await onApprovalChange(userId, approval?.approved ?? false, newNote);
      showApprovalFeedback(userId, 'success');
    } catch (e) {
      console.error('Fehler beim Speichern der Anmerkung:', e);
      showApprovalFeedback(userId, 'error');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, user: UserEntry) => {
    e.stopPropagation();
    setUserToDelete(user);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || !onDeleteUser) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await onDeleteUser(userToDelete.id);
      setUserToDelete(null);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Fehler beim Löschen');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setUserToDelete(null);
    setDeleteError(null);
  };

  // Abteilungen dynamisch aus den Usern ableiten
  const departmentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const noDeptKey = '__none__';
    for (const u of users) {
      const key = u.department ?? noDeptKey;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [users]);

  const statusCounts = useMemo(() => {
    const counts: Record<EmploymentStatus, number> = { active: 0, 'ending-soon': 0, departed: 0 };
    for (const u of users) {
      counts[getEmploymentStatus(u)]++;
    }
    return counts;
  }, [users]);

  const [exportFilter, setExportFilter] = useState<'all' | 'included' | 'excluded'>('all');

  const exportCounts = useMemo(() => {
    let included = 0;
    let excluded = 0;
    for (const u of users) {
      if (u.excludeFromExport) excluded++; else included++;
    }
    return { included, excluded };
  }, [users]);

  const hasActiveFilters = selectedDepartments.size > 0 || selectedStatuses.size > 0 || exportFilter !== 'all';

  const toggleDepartment = (dept: string) => {
    setSelectedDepartments((prev) => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept); else next.add(dept);
      return next;
    });
  };

  const toggleStatus = (status: EmploymentStatus) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  };

  const resetFilters = () => {
    setSelectedDepartments(new Set());
    setSelectedStatuses(new Set());
    setExportFilter('all');
  };

  const filteredUsers = users.filter((user) => {
    // Textsuche
    const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name;
    const matchesSearch =
      searchTerm === '' ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    // Abteilungsfilter
    if (selectedDepartments.size > 0) {
      const userDept = user.department ?? '__none__';
      if (!selectedDepartments.has(userDept)) return false;
    }

    // Statusfilter
    if (selectedStatuses.size > 0) {
      if (!selectedStatuses.has(getEmploymentStatus(user))) return false;
    }

    // Export-Filter
    if (exportFilter === 'included' && user.excludeFromExport) return false;
    if (exportFilter === 'excluded' && !user.excludeFromExport) return false;

    return true;
  });

  const displayName = (user: UserEntry) =>
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name;

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const roleBadge = (user: UserEntry) => {
    if (user.isHR) return <Badge variant="purple" className="px-1.5 py-0.5 text-[10px] rounded">HR</Badge>;
    if (user.isManager) return <Badge variant="info" className="px-1.5 py-0.5 text-[10px] rounded">Manager</Badge>;
    return null;
  };

  const employmentTypeBadge = (user: UserEntry) => {
    if (!user.employmentType) return null;
    const label = EMPLOYMENT_TYPE_LABELS[user.employmentType];
    if (!label) return null;
    return (
      <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-medium rounded border-teal-200 bg-teal-100 text-teal-700 dark:border-teal-800 dark:bg-teal-900/40 dark:text-teal-300">
        {label}
      </Badge>
    );
  };

  const employmentEndBadge = (user: UserEntry) => {
    if (!user.employmentEnd) return null;
    const endDate = new Date(user.employmentEnd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (endDate.getTime() === today.getTime()) {
      return (
        <Badge variant="warning" className="px-1.5 py-0.5 text-[10px] rounded">
          Letzter Tag
        </Badge>
      );
    }
    if (endDate < today) {
      return (
        <Badge variant="destructive" className="px-1.5 py-0.5 text-[10px] rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
          Ausgeschieden
        </Badge>
      );
    }
    // Enddatum in der Zukunft
    const formatted = endDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return (
      <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-medium rounded">
        Endet am {formatted}
      </Badge>
    );
  };

  const statusBadges = (user: UserEntry) => (
    <>
      {employmentEndBadge(user)}
      {user.excludeFromExport && (
        <Badge variant="secondary" className="gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded" title="Vom Lohn-Export ausgeschlossen">
          <EyeOff className="w-2.5 h-2.5" />
          Export
        </Badge>
      )}
    </>
  );

  const mfaBadge = (user: UserEntry) => {
    if (!isHR) return null;
    const mfaUser = mfaOverview.get(user.id);
    if (!mfaUser) return null;
    const status: MfaStatus = mfaUser.status;
    const variant = getStatusBadgeVariant(`mfa_${status}`);
    const label = MFA_STATUS_LABELS[status];
    return (
      <Badge variant={variant} className="px-1.5 py-0.5 text-[10px] rounded">
        {label}
      </Badge>
    );
  };

  const mfaActionButtons = (user: UserEntry) => {
    if (!isHR) return null;
    return (
      <>
        <button
          onClick={(e) => { e.stopPropagation(); setMfaAction({ type: 'reset', user }); }}
          className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
          title="MFA zurücksetzen"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setMfaAction({ type: 'force', user }); }}
          className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          title="MFA erzwingen"
        >
          <ShieldAlert className="w-4 h-4" />
        </button>
      </>
    );
  };

  const actionButtons = (user: UserEntry, size: 'sm' | 'md' = 'sm') => {
    const iconSize = size === 'md' ? 'w-4.5 h-4.5' : 'w-4 h-4';
    const padding = size === 'md' ? 'p-2' : 'p-1.5';
    return (
      <>
        {onCalendarEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onCalendarEdit(user); }}
            className={`${padding} text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors`}
            title="Kalender bearbeiten"
          >
            <Calendar className={iconSize} />
          </button>
        )}
        {onDeleteUser && showDeleteButton && (
          <button
            onClick={(e) => handleDeleteClick(e, user)}
            className={`${padding} text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400
                       hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors`}
            title="Mitarbeiter löschen"
          >
            <Trash2 className={iconSize} />
          </button>
        )}
      </>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            <span>Lade Mitarbeiterliste...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {/* Suchfeld + Ansicht-Umschalter */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <Input
            type="text"
            placeholder="Mitarbeiter suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4"
          />
        </div>
        <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 transition-colors ${viewMode === 'grid'
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title="Kachelansicht"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 transition-colors ${viewMode === 'list'
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title="Listenansicht"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter-Chips */}
      {users.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Abteilung:</span>
            {Array.from(departmentCounts.entries())
              .sort(([a], [b]) => (a === '__none__' ? 1 : b === '__none__' ? -1 : a.localeCompare(b, 'de')))
              .map(([dept, count]) => {
                const isActive = selectedDepartments.has(dept);
                const label = dept === '__none__' ? 'Keine Abteilung' : dept;
                return (
                  <button
                    key={dept}
                    onClick={() => toggleDepartment(dept)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors
                      ${isActive
                        ? 'bg-blue-600 text-white dark:bg-blue-500'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                  >
                    {label}
                    <span className={`text-[10px] ${isActive ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Status:</span>
            {([
              { key: 'active' as EmploymentStatus, label: 'Aktiv', activeClass: 'bg-green-600 text-white dark:bg-green-500', badgeActive: 'text-green-200' },
              { key: 'ending-soon' as EmploymentStatus, label: 'Endet bald', activeClass: 'bg-amber-500 text-white dark:bg-amber-500', badgeActive: 'text-amber-200' },
              { key: 'departed' as EmploymentStatus, label: 'Ausgeschieden', activeClass: 'bg-red-600 text-white dark:bg-red-500', badgeActive: 'text-red-200' },
            ]).map(({ key, label, activeClass, badgeActive }) => {
              const isActive = selectedStatuses.has(key);
              const count = statusCounts[key];
              return (
                <button
                  key={key}
                  onClick={() => toggleStatus(key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors
                    ${isActive
                      ? activeClass
                      : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                  {label}
                  <span className={`text-[10px] ${isActive ? badgeActive : 'text-gray-400 dark:text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="ml-2 text-xs text-gray-700 dark:text-gray-300 hover:underline"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Export:</span>
            {([
              { key: 'all' as const, label: 'Alle', count: users.length },
              { key: 'included' as const, label: 'Im Export', count: exportCounts.included },
              { key: 'excluded' as const, label: 'Ausgeschlossen', count: exportCounts.excluded },
            ]).map(({ key, label, count }) => {
              const isActive = exportFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setExportFilter(key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors
                    ${isActive
                      ? 'bg-gray-700 text-white dark:bg-gray-500'
                      : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                  {label}
                  <span className={`text-[10px] ${isActive ? 'text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Monatsselektor für Freigaben (nur Listenansicht + HR) */}
      {viewMode === 'list' && isHR && approvalMonth && onApprovalMonthChange && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Abrechnungsmonat:</span>
          <button
            onClick={() => onApprovalMonthChange(prevPayrollMonth(approvalMonth))}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Vorheriger Monat"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[130px] text-center">
            {formatPayrollMonth(approvalMonth)}
          </span>
          <button
            onClick={() => onApprovalMonthChange(nextPayrollMonth(approvalMonth))}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Nächster Monat"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Inhalt */}
      <div>
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {searchTerm || hasActiveFilters ? (
              <p>Keine Mitarbeiter gefunden{searchTerm ? <> für &ldquo;{searchTerm}&rdquo;</> : ''}{hasActiveFilters ? ' mit den gewählten Filtern' : ''}</p>
            ) : (
              <p>Noch keine Mitarbeiter registriert</p>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Kachelansicht */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-4">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => onSelectUser(user.id)}
                className={`relative group rounded-lg border p-4 cursor-pointer transition-all
                           hover:border-gray-300 dark:hover:border-white/20
                           ${selectedUserId === user.id
                    ? 'border-gray-900 dark:border-gray-100 bg-gray-50 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900'}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full
                                  flex items-center justify-center mb-3">
                    <User className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate w-full">
                    {displayName(user)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate w-full mt-0.5">
                    {user.email}
                  </p>
                  {user.personnelNumber && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      Pers.-Nr. {user.personnelNumber}
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap justify-center">
                    {user.department && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                        {user.department}
                      </span>
                    )}
                    {employmentTypeBadge(user)}
                    {roleBadge(user)}
                    {statusBadges(user)}
                    {mfaBadge(user)}
                  </div>
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(user.lastActive)}</span>
                  </div>
                </div>
                {/* Aktionen */}
                <div className="flex items-center justify-center gap-0.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {actionButtons(user, 'md')}
                  {mfaActionButtons(user)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Listenansicht */
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredUsers.map((user) => (
              <li key={user.id}>
                <div
                  className={`w-full px-4 py-3 flex items-center gap-3
                             transition-colors hover:bg-gray-50 dark:hover:bg-gray-700
                             ${selectedUserId === user.id ? 'bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-900 dark:border-gray-100' : ''}`}
                >
                  <button
                    onClick={() => onSelectUser(user.id)}
                    className="flex-1 flex items-center gap-3 text-left min-w-0"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full
                                    flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {displayName(user)}
                        </p>
                        {employmentTypeBadge(user)}
                        {roleBadge(user)}
                        {statusBadges(user)}
                        {mfaBadge(user)}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        {user.personnelNumber && (
                          <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
                            Pers.-Nr. {user.personnelNumber}
                          </span>
                        )}
                        {user.department && (
                          <span className="flex-shrink-0 inline-block px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            {user.department}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(user.lastActive)}</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </button>
                  {/* Freigabe-Bereich (nur HR + Listenansicht) */}
                  {isHR && onApprovalChange && (() => {
                    const approval = approvalMap.get(user.id);
                    const status = saveStatus[user.id];
                    return (
                      <div className="flex-shrink-0 flex items-center gap-3 min-w-[320px]" onClick={(e) => e.stopPropagation()}>
                        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap shrink-0">
                          <input
                            type="checkbox"
                            checked={approval?.approved ?? false}
                            onChange={(e) => handleCheckboxChange(user.id, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-green-500 focus:ring-green-500"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {approval?.approved ? 'Freigegeben' : 'Freigeben'}
                          </span>
                          {status === 'success' && (
                            <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          )}
                          {status === 'error' && (
                            <AlertCircleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          )}
                        </label>
                        <input
                          type="text"
                          placeholder="Anmerkung..."
                          value={localNotes[user.id] ?? ''}
                          onChange={(e) => setLocalNotes((prev) => ({ ...prev, [user.id]: e.target.value }))}
                          onBlur={() => handleNoteBlur(user.id)}
                          maxLength={500}
                          className="text-xs px-2 py-1 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                                     text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500
                                     focus:ring-1 focus:ring-gray-500 focus:border-gray-500 flex-1 min-w-0"
                        />
                      </div>
                    );
                  })()}
                  {mfaActionButtons(user)}
                  {actionButtons(user)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-white/10 text-xs text-gray-500 dark:text-gray-400">
        {filteredUsers.length} von {users.length} Mitarbeiter
        {users.length !== 1 ? 'n' : ''}
      </div>

      {/* Bestätigungsdialog */}
      <Dialog open={!!userToDelete} onOpenChange={(open) => { if (!open) handleCancelDelete(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <DialogTitle>Mitarbeiter löschen</DialogTitle>
            </div>
            <DialogDescription>
              Möchten Sie den folgenden Mitarbeiter wirklich löschen?
            </DialogDescription>
          </DialogHeader>

          {userToDelete && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{displayName(userToDelete)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{userToDelete.email}</p>
                  </div>
                </div>
              </div>

              <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Achtung:</strong> Diese Aktion löscht alle Daten des Mitarbeiters
                  (Einstellungen, Zeiteinträge und Urlaubsüberträge) unwiderruflich aus der Datenbank.
                </p>
              </div>

              {deleteError && (
                <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-3">
                  <p className="text-sm text-red-700 dark:text-red-300">{deleteError}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              variant="outline"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              variant="destructive"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Lösche...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Endgültig löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MFA-Bestätigungsdialog */}
      <Dialog open={!!mfaAction} onOpenChange={(open) => { if (!open) { setMfaAction(null); setMfaActionError(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${mfaAction?.type === 'reset' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                {mfaAction?.type === 'reset'
                  ? <RotateCcw className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  : <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
              </div>
              <DialogTitle>
                {mfaAction?.type === 'reset' ? 'MFA zurücksetzen' : 'MFA erzwingen'}
              </DialogTitle>
            </div>
            <DialogDescription>
              {mfaAction?.type === 'reset'
                ? 'Die MFA-Einrichtung dieses Mitarbeiters wird zurückgesetzt. Der Mitarbeiter erhält eine neue Grace Period zur Neueinrichtung.'
                : 'MFA wird für diesen Mitarbeiter sofort erzwungen. Der Mitarbeiter muss MFA beim nächsten Login einrichten.'}
            </DialogDescription>
          </DialogHeader>

          {mfaAction && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{displayName(mfaAction.user)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{mfaAction.user.email}</p>
                </div>
              </div>
            </div>
          )}

          {mfaActionError && (
            <p
              role="alert"
              className="text-sm text-destructive bg-destructive/10 border border-destructive/50 rounded-md px-3 py-2"
            >
              {mfaActionError}
            </p>
          )}

          <DialogFooter>
            <Button
              onClick={() => { setMfaAction(null); setMfaActionError(null); }}
              disabled={isMfaActionPending}
              variant="outline"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleMfaAction}
              disabled={isMfaActionPending}
              variant={mfaAction?.type === 'reset' ? 'destructive' : 'default'}
            >
              {isMfaActionPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Wird ausgeführt...
                </>
              ) : mfaAction?.type === 'reset' ? (
                <>
                  <RotateCcw className="w-4 h-4" />
                  Zurücksetzen
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Erzwingen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
